import { useState, useContext, useEffect, useCallback } from "react";
import SeamService from "../../services/SeamService";
import { AppContexts } from "../../providers";

const LockManagementPortal = () => {
  const [portalUrl, setPortalUrl] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seamEnabled, setSeamEnabled] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    document.title = "Smart Locks - RueBaRue";
  }, []);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await SeamService.getSettings();
      setSeamEnabled(data.seam_enabled || false);
    } catch (err) {
      console.error("Error loading Seam settings:", err);
    } finally {
      setPageLoading(false);
    }
  };

  // ── Load Customer Portal ────────────────────────────────────────────
  const loadPortal = useCallback(async () => {
    if (!seamEnabled) return;
    try {
      setPortalLoading(true);
      setError(null);
      const { data } = await SeamService.createPortal();
      setPortalUrl(data.url);
    } catch (err) {
      console.error("Error creating portal:", err);
      setError("Failed to load the smart locks management portal.");
    } finally {
      setPortalLoading(false);
    }
  }, [seamEnabled]);

  // Auto-load portal when component mounts
  useEffect(() => {
    if (seamEnabled && !portalUrl) {
      loadPortal();
    }
  }, [seamEnabled, portalUrl, loadPortal]);

  if (pageLoading) {
    return (
      <div
        id="hms-main-body"
        style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}
      >
        <div uk-spinner="ratio: 2"></div>
      </div>
    );
  }

  return (
    <section id="hms-lock-management-template">
      <div id="hms-page-title">
        <h1 className="uk-heading-small">Smart Locks</h1>
      </div>
      <div id="hms-main-body">
        <div className="uk-width-1-1">
          <div className="uk-card hms-left-tabs-content-card mx-w-995">
            {!seamEnabled ? (
              <div>
                <p className="uk-margin-small-top">
                  Enable Smart Locks to automatically generate and manage time-bound door codes for your guests, manage all your connected locks from one central portal, reduce check-in friction, and enhance security with automatic code expiration for every reservation.
                </p>
                <div className="uk-margin-large-top">
                  <h4>Pricing</h4>
                  <p>
                    Smart Locks are billed at $5 per connected property/lock per month. Billing is based on the maximum number of locks connected during the month.
                  </p>
                </div>
                <div className="uk-margin-large-top">
                  <h4>Activate</h4>
                  <p>
                    Go to Settings &gt; <a href="/settings/smart-locks">Smart Locks</a> to enable this feature. 
                  </p>
                  <p>
                    Learn more in our{" "}
                    <a
                      href="https://intercom.help/ruebarueapp/en/articles/15055147-smart-locks"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Smart Locks knowledge base article
                    </a>
                    .
                  </p>
                  <p>
                    Need help? Contact{" "}
                    <a href="mailto:support@ruebarue.com">RueBaRue support</a>.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Error Alert */}
                {error && (
                  <div className="uk-alert uk-alert-danger" uk-alert="" style={{ display: "flex", alignItems: "center", minHeight: "52px" }}>
                    <p style={{ margin: 0, flex: 1 }}>{error}</p>
                    <a
                      className="uk-alert-close"
                      uk-close=""
                      onClick={() => setError(null)}
                      style={{ minWidth: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}
                    ></a>
                  </div>
                )}

                <p className="secondary uk-margin-bottom">
                  Use the portal below to connect your smart lock accounts and
                  assign locks to your properties. Changes are saved automatically.
                </p>

                {portalLoading ? (
                  <div className="uk-text-center uk-padding">
                    <div uk-spinner="ratio: 1"></div>
                    <p className="uk-text-muted">
                      Loading smart locks management portal...
                    </p>
                  </div>
                ) : portalUrl ? (
                  <div
                    style={{
                      border: "1px solid #e5e5e5",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <iframe
                      src={portalUrl}
                      title="Seam Smart Lock Portal"
                      style={{
                        width: "100%",
                        height: "700px",
                        border: "none",
                      }}
                      allow="clipboard-write"
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation"
                    />
                  </div>
                ) : (
                  <div className="uk-text-center uk-padding">
                    <p className="uk-text-muted">
                      Could not load the portal.{" "}
                      <a onClick={loadPortal} style={{ cursor: "pointer" }}>
                        Try again
                      </a>
                    </p>
                  </div>
                )}

                <button
                  className="uk-button hms-btn hms-white-btn uk-margin-small-top"
                  onClick={() => {
                    setPortalUrl(null);
                    loadPortal();
                  }}
                >
                  Refresh Portal
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LockManagementPortal;
